import { useMemo } from 'react'
import type { SearchEvent } from '@shared/searchIndex'
import { isUpcoming } from '../lib/searchRanking'
import { useSearchIndex } from './useSearchIndex'
import { useHomeRegion } from './useHomeRegion'
import { useNow } from './useNow'

export interface ArtistNextAppearance {
  /** The artist's soonest upcoming event — in the viewer's region when there is one. */
  event: SearchEvent
  /** `event` is in the viewer's home region (always true when browsing everywhere). */
  near: boolean
}

const EMPTY: Map<string, ArtistNextAppearance> = new Map()

/**
 * Where each of `artistIds` plays next, read off the search index (no Firestore
 * reads). An artist with nothing upcoming is absent from the map.
 */
export function useArtistNextAppearances(artistIds: ReadonlySet<string>) {
  const { index, loading } = useSearchIndex()
  const { regionId, isEverywhere } = useHomeRegion()
  const now = useNow()

  const appearances = useMemo(() => {
    if (!index || artistIds.size === 0) return EMPTY
    const out = new Map<string, ArtistNextAppearance>()
    for (const artist of index.artists) {
      if (!artistIds.has(artist.id)) continue
      let nearest: SearchEvent | undefined
      let soonest: SearchEvent | undefined
      for (const ei of artist.eventIdx) {
        const event = index.events[ei]
        if (!event || event.cancelled || !isUpcoming(event, now)) continue
        if (!soonest || event.startDate < soonest.startDate) soonest = event
        const near = isEverywhere || event.regionId === regionId
        if (near && (!nearest || event.startDate < nearest.startDate)) nearest = event
      }
      if (nearest) out.set(artist.id, { event: nearest, near: true })
      else if (soonest) out.set(artist.id, { event: soonest, near: false })
    }
    return out
  }, [index, artistIds, regionId, isEverywhere, now])

  return { appearances, loading }
}
